import { cn } from "@web/lib/utils.ts";
import {
  LayersIcon,
  visibleLayerDefs,
} from "@web/features/globe/layers.ts";
import { useGlobeStore } from "@web/stores/globe.ts";
import { ChevronUp } from "lucide-react";

/**
 * Compact vertical icon strip — top-left of the globe canvas.
 * Driven by the layer registry; no per-layer branching here.
 */
export function LayerStrip({ hidden }: { hidden?: boolean }) {
  const layers = useGlobeStore((s) => s.layers);
  const collapsed = useGlobeStore((s) => s.layersCollapsed);
  const setCollapsed = useGlobeStore((s) => s.setLayersCollapsed);
  const toggleLayer = useGlobeStore((s) => s.toggleLayer);

  if (hidden) return null;

  const defs = visibleLayerDefs(layers);

  if (collapsed) {
    return (
      <div className="pointer-events-auto">
        <button
          type="button"
          title="Layers"
          aria-label="Expand layers"
          className={cn(
            "flex size-8 items-center justify-center rounded-md border border-line bg-panel/90 text-muted backdrop-blur-sm",
            "hover:border-accent/40 hover:text-text",
          )}
          onClick={() => setCollapsed(false)}
        >
          <LayersIcon className="size-4" />
        </button>
      </div>
    );
  }

  return (
    <div className="pointer-events-auto flex flex-col gap-1 rounded-md border border-line bg-panel/90 p-1 backdrop-blur-sm">
      <button
        type="button"
        title="Collapse layers"
        aria-label="Collapse layers"
        className="flex size-7 items-center justify-center rounded text-faint hover:bg-row-hover hover:text-text"
        onClick={() => setCollapsed(true)}
      >
        <ChevronUp className="size-3.5" />
      </button>
      {defs.map((def) => {
        const on = layers[def.id];
        const Icon = def.icon;
        return (
          <button
            key={def.id}
            type="button"
            title={def.label}
            aria-label={def.label}
            aria-pressed={on}
            className={cn(
              "flex size-7 items-center justify-center rounded transition-colors",
              on ? "text-accent" : "text-faint hover:text-muted",
              "hover:bg-row-hover",
            )}
            onClick={() => toggleLayer(def.id)}
          >
            <Icon className="size-3.5" />
          </button>
        );
      })}
    </div>
  );
}
